import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { Alert, AlertDescription } from '@/components/ui/alert';
import type { MutualFundDividend } from '@/types/mutualFundDividend';
import { formatNumber } from '@/lib/currency.utils';

const PAGE_SIZE = 100;

export interface MutualFundDividendTableProps {
  dividends: MutualFundDividend[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  page: number;
  onPageChange: (page: number) => void;
  totalCount: number;
}

export function MutualFundDividendTable({
  dividends,
  isLoading,
  isError,
  onRetry,
  page,
  onPageChange,
  totalCount,
}: MutualFundDividendTableProps) {
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const showPagination = totalCount > PAGE_SIZE;

  if (isLoading) {
    return (
      <div className="space-y-2" data-testid="loading-state" aria-busy="true" aria-live="polite">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <Alert variant="destructive" role="alert">
        <AlertCircle className="h-4 w-4" aria-hidden="true" />
        <AlertDescription className="flex items-center justify-between gap-4">
          <span>Failed to load dividend payments.</span>
          <Button variant="outline" size="sm" onClick={onRetry}>
            Retry
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  if (dividends.length === 0) {
    return (
      <div
        className="flex items-center justify-center py-12 text-muted-foreground text-sm text-center px-4"
        data-testid="empty-state"
      >
        No dividend payments have been recorded for this fund.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto w-full">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Payment Date</TableHead>
              <TableHead className="text-right">Dividend / Unit</TableHead>
              <TableHead className="text-right">Total Amount</TableHead>
              <TableHead>Currency</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {dividends.map((dividend) => (
              <TableRow key={dividend.id}>
                <TableCell>{dividend.payment_date}</TableCell>
                <TableCell className="text-right">
                  {formatNumber(dividend.dividend_per_unit, 6)}
                </TableCell>
                <TableCell className="text-right">
                  {formatNumber(dividend.amount, 2)}
                </TableCell>
                <TableCell>{dividend.currency}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {showPagination && (
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={() => onPageChange(page - 1)}
                aria-disabled={page <= 1}
                className={page <= 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
              />
            </PaginationItem>
            <PaginationItem>
              <span className="px-4 py-2 text-sm">
                Page {page} of {totalPages}
              </span>
            </PaginationItem>
            <PaginationItem>
              <PaginationNext
                onClick={() => onPageChange(page + 1)}
                aria-disabled={page >= totalPages}
                className={page >= totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </div>
  );
}
